import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, BadgeCheck } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";

function ProjectShowcase({ project }) {
  const [active, setActive] = useState(0);
  const imgs = project.images && project.images.length ? project.images : [];
  const labels = project.image_labels || [];
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-testid={`project-${project.id}`}>
      <div className="relative">
        <div className="aspect-[4/3] overflow-hidden border border-slate-800 bg-slate-900">
          {imgs[active] && <img key={active} src={mediaUrl(imgs[active])} alt={labels[active] || project.title} loading="lazy" className="w-full h-full object-cover animate-[fadeIn_0.4s_ease]" />}
        </div>
        {labels[active] && (
          <div className="absolute bottom-3 left-3 bg-slate-950/80 backdrop-blur-sm border border-slate-700 px-3 py-1.5 text-xs font-semibold" data-testid="gallery-caption">{labels[active]}</div>
        )}
        {imgs.length > 1 && (
          <div className="flex gap-2 mt-3">
            {imgs.map((g, i) => (
              <button key={i} onClick={() => setActive(i)} aria-label={labels[i] || `imagine ${i + 1}`}
                className={`relative w-1/4 aspect-[4/3] overflow-hidden border transition-all ${i === active ? "border-orange-600 ring-1 ring-orange-600" : "border-slate-800 opacity-60 hover:opacity-100"}`}
                data-testid={`gallery-thumb-${i}`}>
                <img src={mediaUrl(g)} alt="" loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        {project.partner && (
          <div className="absolute -top-3 left-4 bg-orange-600 text-white px-4 py-2 shadow-xl">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">Reprezentant oficial</div>
            <div className="text-base font-black tracking-tight">{project.partner}</div>
          </div>
        )}
      </div>

      <div>
        {project.badge && (
          <span className="inline-flex items-center gap-2 border border-orange-900/60 bg-orange-950/30 text-orange-400 text-xs font-bold uppercase tracking-wider px-3 py-1.5 mb-5" data-testid="case-study-badge">
            <BadgeCheck className="w-4 h-4" /> {project.badge}
          </span>
        )}
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05]">{project.title}</h2>
        {project.summary && <p className="text-slate-400 mt-6 leading-relaxed text-lg whitespace-pre-line">{project.summary}</p>}

        {project.steps && project.steps.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800 mt-10">
            {project.steps.map((s, i) => (
              <div key={i} className="bg-slate-950 p-5 group hover:bg-slate-900 transition-colors">
                <div className="flex items-center justify-between">
                  <Wrench className="w-5 h-5 text-orange-500" />
                  <span className="font-mono text-xs text-slate-600 group-hover:text-orange-500 transition-colors">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-bold mt-3">{s.title}</h3>
                {s.desc && <p className="text-sm text-slate-400 mt-1 leading-relaxed">{s.desc}</p>}
              </div>
            ))}
          </div>
        )}

        <Link to="/contact" className="inline-flex items-center gap-2 mt-10 bg-orange-600 hover:bg-orange-700 text-white px-7 py-4 font-bold uppercase tracking-wide text-sm transition-colors group" data-testid="case-study-cta">
          Discută un proiect similar
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
}

export default function CaseStudy() {
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  if (!projects.length) return null;
  const [featured, ...rest] = projects;

  return (
    <section className="bg-slate-950 text-white relative overflow-hidden" data-testid="case-study-section">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(234,88,12,0.12),transparent_45%)]" />
      <div className="max-w-7xl mx-auto px-6 py-24 relative">
        <div className="flex items-center gap-3 mb-12">
          <span className="h-px w-12 bg-orange-600" />
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-orange-500">Proiecte de referință</span>
        </div>

        <ProjectShowcase project={featured} />

        {rest.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-16">
            {rest.map((p) => (
              <div key={p.id} className="border border-slate-800 bg-slate-900/40 hover:border-orange-600/60 transition-colors" data-testid={`project-card-${p.id}`}>
                {p.images[0] && <div className="aspect-[16/10] overflow-hidden"><img src={mediaUrl(p.images[0])} alt={p.title} loading="lazy" className="w-full h-full object-cover" /></div>}
                <div className="p-5">
                  {p.partner && <span className="text-[10px] uppercase tracking-wider font-bold text-orange-500">{p.partner}</span>}
                  <h3 className="font-bold mt-1 leading-snug">{p.title}</h3>
                  {p.summary && <p className="text-sm text-slate-400 mt-2 line-clamp-3">{p.summary}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
